/**
 * LivedThroughPanel — the verified "world this person lived in" layer.
 *
 * Pulls GET /api/persons/:id/historical-events — rows from our curated
 * historical_events table, ranked by severity × geographic specificity.
 * Each chip shows the event title, year span, and geo scope badge. Click
 * expands to reveal the summary and source links.
 *
 * Design notes:
 *   - Silent on empty. If the person has no birth/death years we get an
 *     empty list; rendering nothing is better than a "nothing here yet"
 *     box that adds visual noise to the profile.
 *   - Chips are subtle by default — this is ambient context, not a
 *     demand for the reader's attention. Expansion surfaces the detail
 *     when the reader is curious.
 *   - Severity colour-codes the left stripe so eye can skim the list and
 *     pick out the society-altering events.
 */
import { useEffect, useState } from 'react'
import { api } from '../../services/api.js'

const TYPE_ICON = {
  war:       '⚔️',
  famine:    '🍞',
  epidemic:  '🦠',
  flood:     '🌊',
  migration: '🧳',
  policy:    '📜',
  economic:  '📉',
  other:     '📅',
}

const SEVERITY_STRIPE = {
  5: '#991b1b',  // crimson — society-altering
  4: '#b45309',  // amber
  3: '#4b5563',  // slate
  2: '#9ca3af',
  1: '#d1d5db',
}

export default function LivedThroughPanel({ personId }) {
  const [events, setEvents] = useState(null)
  const [expandedId, setExpanded] = useState(null)

  useEffect(() => {
    if (!personId) return
    api.get(`/persons/${personId}/historical-events`)
      .then(setEvents)
      .catch(() => setEvents([]))
  }, [personId])

  if (events === null || events.length === 0) return null

  return (
    <div className="lived-through">
      <div className="lived-through__header">
        <div className="page-header__label">Lived through</div>
        <div className="lived-through__sub">
          Verified events that overlap this person's life window and place.
        </div>
      </div>

      <div className="lived-through__list">
        {events.map(e => {
          const open = expandedId === e.id
          const years = e.start_year === e.end_year
            ? `${e.start_year}`
            : `${e.start_year}–${e.end_year}`
          const scope =
            e.geo_match === 'place'   ? (e.place || e.region) :
            e.geo_match === 'region'  ? e.region :
            e.geo_match === 'country' ? countryLabel(e.country) :
            'worldwide'
          // Hedged tail: the person had no recorded death date and this event
          // falls past a confident lifespan — we can't assert they lived
          // through it, only that they might have.
          const maybe = e.life_certainty === 'maybe'
          return (
            <div
              key={e.id}
              className={`lived-through__item ${open ? 'lived-through__item--open' : ''} ${maybe ? 'lived-through__item--maybe' : ''}`}
              style={{ '--stripe': SEVERITY_STRIPE[e.severity] || SEVERITY_STRIPE[3] }}
            >
              <button
                className="lived-through__row"
                onClick={() => setExpanded(open ? null : e.id)}
              >
                <span className="lived-through__icon" aria-hidden>{TYPE_ICON[e.event_type] || '📅'}</span>
                <span className="lived-through__title">
                  {e.title}
                  {maybe && <span className="lived-through__maybe" title="No death date on record — they may have died before this">may have witnessed</span>}
                </span>
                <span className="lived-through__years">{years}</span>
                <span className="lived-through__scope">{scope}</span>
              </button>
              {open && (
                <div className="lived-through__body">
                  <p>{e.summary}</p>
                  {e.narrative && <p className="lived-through__narrative">{e.narrative}</p>}
                  {e.source_urls?.length > 0 && (
                    <div className="lived-through__sources">
                      {e.source_urls.map((u, i) => (
                        <a key={i} href={u} target="_blank" rel="noreferrer" className="lived-through__source">
                          Source{e.source_urls.length > 1 ? ` ${i + 1}` : ''} ↗
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function countryLabel(iso) {
  if (!iso) return ''
  const MAP = { NL: 'Netherlands', BE: 'Belgium', DE: 'Germany', FR: 'France', GB: 'United Kingdom', US: 'United States' }
  return MAP[iso] || iso
}
